import React, { useEffect, useState } from "react";
import { Plug, Plus, X, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { PageHeader, Loading, DataTable, StatusBadge, SandboxTag } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Integrations() {
  const { t } = useI18n();
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState(null);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", type: "API", description: "" });
  const [saving, setSaving] = useState(false);

  const load = () => { api.get("/integrations").then((r) => setRows(r.data)); };
  useEffect(load, []);
  if (!rows) return <Loading />;

  const create = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post("/integrations", form); toast.success("Sandbox integration registered"); setShow(false); setForm({ name: "", type: "API", description: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const cols = [
    { key: "name", label: t("name"), render: (r) => <span className="font-medium">{r.name}</span> },
    { key: "type", label: "Type" }, { key: "description", label: "Description" },
    { key: "status", label: t("status"), render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <div>
      <PageHeader title={t("nav.integrations")} subtitle="Register sandbox-only APIs, tools, models, agents and connectors" summary="Integrations. Administrators can register sandbox-only functions and connectors. No real external systems are connected.">
        <SandboxTag text="SANDBOX ONLY" />
        {isAdmin && <button data-testid="add-integration-btn" onClick={() => setShow(true)} className="qatar-gradient inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> {t("add_integration")}</button>}
      </PageHeader>

      {rows.length === 0 ? (
        <div className="grid place-items-center rounded-2xl border border-dashed border-slate-300 bg-white py-20 text-slate-400"><Plug className="mb-3 h-10 w-10" /><p className="text-sm">No sandbox integrations yet. {isAdmin ? "Register one to get started." : "Only administrators can register integrations."}</p></div>
      ) : <DataTable testid="integrations-table" columns={cols} rows={rows} pageSize={15} />}

      {show && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setShow(false)}>
          <div data-testid="add-integration-modal" className="w-full max-w-md rounded-2xl bg-white p-6 card-shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between"><h3 className="text-lg font-bold font-head">Register Sandbox Integration</h3><button onClick={() => setShow(false)} className="rounded-lg p-1.5 hover:bg-slate-100"><X className="h-5 w-5" /></button></div>
            <form onSubmit={create} className="space-y-3">
              <input data-testid="int-name" placeholder="Integration name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              <select data-testid="int-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">{["API", "Tool", "Model", "Agent", "Data Source", "Mock System", "Connector"].map((o) => <option key={o}>{o}</option>)}</select>
              <textarea data-testid="int-desc" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              <button data-testid="int-create" type="submit" disabled={saving} className="qatar-gradient flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold text-white disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Register (Sandbox Only)</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
